import { Box } from "@outschool/backpack";
import {
  ActivityCurriculum,
  ActivityStandard,
  Search
} from "@outschool/business-rules";
import { useTranslation } from "@outschool/localization";
import {
  BooleanFilters,
  ClassSizeFilter,
  Filter,
  useLanguageString
} from "@outschool/ui-components-website";
import { Checkbox } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import * as Activity from "../../../shared/Activity";
import { useActivityString } from "../../../shared/hooks/useActivityString";
import FilterActionButtons from "./desktop/FilterActionButtons";
import { LevelFilter } from "./desktop/LevelFilters";

export type UncommonFiltersProps = {
  value: {
    enabledFormatFilters?: Array<string>;
    enabledDeliveryFilters?: Array<string>;
    enabledLanguageFilters?: Array<string>;
    enabledUSGradeLevelFilters?: Array<string>;
    enabledEvaluationFilters?: Array<string>;
    enabledCurriculumFilters?: Array<string>;
    enabledStandardFilters?: Array<string>;
    enabledClassSizeFilters?: Search.CapacityFilterType;
    includeInProgressFixedLengthLiveFormat?: boolean;
  };
  onChange: (newValue: {
    enabledFormatFilters?: Array<string>;
    enabledDeliveryFilters?: Array<string>;
    enabledLanguageFilters?: Array<string>;
    enabledUSGradeLevelFilters?: Array<string>;
    enabledEvaluationFilters?: Array<string>;
    enabledCurriculumFilters?: Array<string>;
    enabledStandardFilters?: Array<string>;
    enabledClassSizeFilters?: Search.CapacityFilterType;
    includeInProgressFixedLengthLiveFormat?: boolean;
  }) => void;
  onCancel: () => void;
  handleChangesExternally?: boolean;
  hideFormat?: boolean;
  hideClassType?: boolean;
  hideChatGroupInsideClassType?: boolean;
  hideUSGrade?: boolean;
  hideClassSize?: boolean;
  hideLanguage?: boolean;
  hideEvaluation?: boolean;
  hideCurriculums?: boolean;
  hideStandards?: boolean;
};

export default function UncommonFilters({
  value: {
    enabledFormatFilters: initialFormatFilters = [],
    enabledDeliveryFilters: initialDeliveryFilters = [],
    enabledLanguageFilters: initialLanguageFilters = [],
    enabledUSGradeLevelFilters: initialUSGradeLevelFilters = [],
    enabledEvaluationFilters: initialEvaluationFilters = [],
    enabledClassSizeFilters: initialClassSizeFilters = {},
    enabledCurriculumFilters: initialCurriculumFilters = [],
    enabledStandardFilters: initialStandardFilters = [],
    includeInProgressFixedLengthLiveFormat:
      includeInProgressFixedLengthLiveFormatIntial = true
  },
  onChange,
  onCancel,
  handleChangesExternally = false,
  hideFormat = false,
  hideClassType = false,
  hideChatGroupInsideClassType = false,
  hideUSGrade = true,
  hideClassSize = true,
  hideLanguage = false,
  hideEvaluation = true,
  hideCurriculums = false,
  hideStandards = false
}: UncommonFiltersProps) {
  const { t } = useTranslation("client\\components\\search\\UncommonFilters");
  const { Delivery, Format, evaluationOptions } = useActivityString();
  const { translatedLanguageFilters } = useLanguageString();
  const [enabledFormatFilters, setEnabledFormatFilters] =
    React.useState<string[]>(initialFormatFilters);

  const [enabledDeliveryFilters, setEnabledDeliveryFilters] = React.useState<
    string[]
  >(initialDeliveryFilters);
  const [enabledLanguageFilters, setEnabledLanguageFilters] = React.useState<
    string[]
  >(initialLanguageFilters);
  const [enabledUSGradeLevelFilters, setEnabledUSGradeLevelFilters] =
    React.useState<string[]>(initialUSGradeLevelFilters);

  const [enabledEvaluationFilters, setEnabledEvaluationFilters] =
    React.useState<string[]>(initialEvaluationFilters);

  const [enabledClassSizeFilters, setEnabledClassSizeFilters] =
    React.useState<Search.CapacityFilterType>(initialClassSizeFilters);

  const [enabledCurriculumFilters, setEnabledCurriculumFilters] =
    React.useState<string[]>(initialCurriculumFilters);
  const [enabledStandardFilters, setEnabledStandardFilters] = React.useState<
    string[]
  >(initialStandardFilters);
  const [
    includeInProgressFixedLengthLiveFormat,
    setIncludeInProgressFixedLengthLiveFormat
  ] = React.useState<boolean>(includeInProgressFixedLengthLiveFormatIntial);

  const isMobile = useIsMobile();

  const deliveryFilters = hideChatGroupInsideClassType
    ? []
    : Delivery.filter(
        option => option.value != Activity.ACTIVITY_DELIVERY.ONE_ON_ONE_CLASS
      );

  return (
    <Box
      flex
      sx={(theme: $TSFixMe) => ({
        flexDirection: "column",
        fontSize: "1.3em",

        [theme.breakpoints.up("sm")]: {
          fontSize: "inherit"
        }
      })}
    >
      <Box
        flex
        sx={
          isMobile
            ? { flexDirection: "column", gap: "1rem" }
            : { marginBottom: handleChangesExternally ? 0 : 24 }
        }
      >
        {!hideFormat && (
          <Box
            sx={
              isMobile
                ? { marginBottom: "0.5em" }
                : {
                    paddingRight: "1em",
                    marginRight: "1em",
                    borderRight: "1px solid",
                    borderColor: "grey.100"
                  }
            }
          >
            <BooleanFilters
              label={t("Format")}
              filters={deliveryFilters}
              enabledFilters={enabledDeliveryFilters}
              setEnabledFilters={setEnabledDeliveryFilters}
              onChange={value => {
                if (handleChangesExternally) {
                  onChange({
                    enabledFormatFilters,
                    enabledDeliveryFilters: value,
                    enabledLanguageFilters,
                    enabledUSGradeLevelFilters,
                    enabledEvaluationFilters,
                    enabledClassSizeFilters,
                    includeInProgressFixedLengthLiveFormat
                  });
                }
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem"
          }}
        >
          <Filter title={t("Class progress")}>
            <Checkbox
              key={"includeInProgressFixedLengthLiveFormat"}
              label={t("Include in-progress classes")}
              name={t("Include in-progress classes")}
              checked={includeInProgressFixedLengthLiveFormat}
              onChange={e => {
                const checked = e.target.checked;
                if (handleChangesExternally) {
                  onChange({
                    enabledFormatFilters,
                    enabledDeliveryFilters,
                    enabledLanguageFilters,
                    enabledUSGradeLevelFilters,
                    enabledEvaluationFilters,
                    enabledClassSizeFilters,
                    enabledCurriculumFilters,
                    enabledStandardFilters,
                    includeInProgressFixedLengthLiveFormat: checked
                  });
                }
                setIncludeInProgressFixedLengthLiveFormat(checked);
              }}
            />
          </Filter>
          {!hideClassType && (
            <Box>
              <BooleanFilters
                label={t("Class type")}
                filters={Format}
                enabledFilters={enabledFormatFilters}
                setEnabledFilters={setEnabledFormatFilters}
                hideFormat={hideFormat}
                onChange={value => {
                  if (handleChangesExternally) {
                    onChange({
                      enabledFormatFilters: value,
                      enabledDeliveryFilters,
                      enabledLanguageFilters,
                      enabledUSGradeLevelFilters,
                      enabledEvaluationFilters,
                      enabledClassSizeFilters,
                      includeInProgressFixedLengthLiveFormat
                    });
                  }
                }}
              />
              {hideFormat && (
                <BooleanFilters
                  label={""}
                  filters={deliveryFilters.filter(
                    option => option.value === Activity.ACTIVITY_DELIVERY.CLUB
                  )}
                  enabledFilters={enabledDeliveryFilters}
                  setEnabledFilters={setEnabledDeliveryFilters}
                  onChange={value => {
                    if (handleChangesExternally) {
                      onChange({
                        enabledFormatFilters,
                        enabledDeliveryFilters: value,
                        enabledLanguageFilters,
                        enabledUSGradeLevelFilters,
                        enabledEvaluationFilters,
                        enabledClassSizeFilters,
                        includeInProgressFixedLengthLiveFormat
                      });
                    }
                  }}
                />
              )}
            </Box>
          )}

          {!hideClassSize && (
            <ClassSizeFilter
              searchFilters={enabledClassSizeFilters}
              onChange={value => {
                setEnabledClassSizeFilters(value);
                onChange({
                  enabledFormatFilters,
                  enabledDeliveryFilters,
                  enabledLanguageFilters,
                  enabledUSGradeLevelFilters,
                  enabledEvaluationFilters,
                  enabledClassSizeFilters: value,
                  includeInProgressFixedLengthLiveFormat
                });
              }}
            />
          )}
          {!hideEvaluation && (
            <BooleanFilters
              label={t("Homework, assessments & grading")}
              filters={evaluationOptions}
              enabledFilters={enabledEvaluationFilters}
              setEnabledFilters={setEnabledEvaluationFilters}
              onChange={value => {
                if (handleChangesExternally) {
                  onChange({
                    enabledFormatFilters,
                    enabledDeliveryFilters,
                    enabledLanguageFilters,
                    enabledUSGradeLevelFilters,
                    enabledEvaluationFilters: value,
                    enabledClassSizeFilters,
                    includeInProgressFixedLengthLiveFormat
                  });
                }
              }}
            />
          )}
          {!hideCurriculums && (
            <BooleanFilters
              label={t("Curriculum")}
              enabledFilters={enabledCurriculumFilters}
              setEnabledFilters={setEnabledCurriculumFilters}
              filters={[
                {
                  label: t("College Board Advanced Placement (AP)"),
                  value: ActivityCurriculum.CollegeBoardAdvancedPlacement
                },
                {
                  label: t("Orton-Gillingham"),
                  value: ActivityCurriculum.OrtonGillingham
                },
                {
                  label: t("Institute for Excellence in Writing (IEW)"),
                  value: ActivityCurriculum.IEW
                },
                {
                  label: t("Eureka Math"),
                  value: ActivityCurriculum.EurekaMath
                },
                {
                  label: t("Saxon Math"),
                  value: ActivityCurriculum.SaxtonMath
                },
                {
                  label: t("Singapore Dimensions Math"),
                  value: ActivityCurriculum.SingaporeDimensionsMath
                },
                {
                  label: t("International Baccalaureate (IB)"),
                  value: ActivityCurriculum.InternationalBaccalaureate
                }
              ]}
              onChange={value => {
                if (handleChangesExternally) {
                  onChange({
                    enabledFormatFilters,
                    enabledDeliveryFilters,
                    enabledLanguageFilters,
                    enabledUSGradeLevelFilters,
                    enabledEvaluationFilters,
                    enabledClassSizeFilters,
                    enabledCurriculumFilters: value,
                    enabledStandardFilters,
                    includeInProgressFixedLengthLiveFormat
                  });
                }
              }}
            />
          )}
          {!hideStandards && (
            <BooleanFilters
              label={t("Standards")}
              enabledFilters={enabledStandardFilters}
              setEnabledFilters={setEnabledStandardFilters}
              filters={[
                {
                  label: t("Common Core State Standards (CCSS)"),
                  value: ActivityStandard.CCSS
                },
                {
                  label: t("Next Generation Science Standards (NGSS)"),
                  value: ActivityStandard.NGSS
                },
                {
                  label: t("Advanced Placement (AP) Standards"),
                  value: ActivityStandard.AP
                },
                {
                  label: t(
                    "National Curriculum Standards for Social Studies (NCSS)"
                  ),
                  value: ActivityStandard.NCSS
                }
              ]}
              onChange={value => {
                if (handleChangesExternally) {
                  onChange({
                    enabledFormatFilters,
                    enabledDeliveryFilters,
                    enabledLanguageFilters,
                    enabledUSGradeLevelFilters,
                    enabledEvaluationFilters,
                    enabledClassSizeFilters,
                    enabledCurriculumFilters,
                    enabledStandardFilters: value,
                    includeInProgressFixedLengthLiveFormat
                  });
                }
              }}
            />
          )}
          {!hideUSGrade && (
            <LevelFilter
              title={t("US grade level")}
              value={enabledUSGradeLevelFilters}
              filterOptions={Activity.usGradeLevels}
              onChange={value => {
                setEnabledUSGradeLevelFilters(value);
                onChange({
                  enabledFormatFilters,
                  enabledDeliveryFilters,
                  enabledLanguageFilters,
                  enabledUSGradeLevelFilters: value,
                  enabledEvaluationFilters,
                  enabledClassSizeFilters,
                  includeInProgressFixedLengthLiveFormat
                });
              }}
              listSx={{
                marginBottom: "1em"
              }}
            />
          )}
          {!hideLanguage && (
            <BooleanFilters
              label={t("Language")}
              filters={translatedLanguageFilters}
              enabledFilters={enabledLanguageFilters}
              setEnabledFilters={setEnabledLanguageFilters}
              onChange={value => {
                if (handleChangesExternally) {
                  onChange({
                    enabledFormatFilters,
                    enabledDeliveryFilters,
                    enabledLanguageFilters: value,
                    enabledUSGradeLevelFilters,
                    enabledEvaluationFilters,
                    enabledClassSizeFilters,
                    includeInProgressFixedLengthLiveFormat
                  });
                }
              }}
            />
          )}
        </Box>
      </Box>
      {handleChangesExternally ? null : (
        <FilterActionButtons
          onApply={() =>
            onChange({
              enabledFormatFilters,
              enabledDeliveryFilters,
              enabledLanguageFilters,
              enabledEvaluationFilters,
              enabledUSGradeLevelFilters,
              enabledClassSizeFilters,
              enabledCurriculumFilters,
              enabledStandardFilters,
              includeInProgressFixedLengthLiveFormat
            })
          }
          onCancel={onCancel}
        />
      )}
    </Box>
  );
}
